import type { AxiosInstance } from 'axios';
import authService from './auth.service';

export interface PublicBlockedCountry {
  country_code: string;
  country_name: string;
  first_blocked_at: string;
  last_verified_at: string;
}


export type LeadSource = 'oauth_google' | 'web_form' | 'imported';
export type LeadStatus = 'pending' | 'invited' | 'converted' | 'rejected' | 'duplicate';

export interface Lead {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  message: string | null;
  source: LeadSource;
  status: LeadStatus;
  vendor_source: string | null;
  vendor_acquired_at: string | null;
  vendor_cost: number | null;
  reviewed_by: string | null;
  reviewer_name: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  resulting_invitation_id: string | null;
  resulting_user_id: string | null;
  created_at: string;
  updated_at: string | null;
}

export type AccessRequestOutcome =
  | 'submitted'
  | 'existing_user'
  | 'already_pending'
  | 'already_invited'
  | 'previously_rejected'
  | 'internal_domain_blocked';

export interface AccessRequestPayload {
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  message?: string;
  /** The version of the privacy notice the person accepted on the form (#289). */
  privacy_notice_version?: number;
}

export interface AccessRequestResponse {
  outcome: AccessRequestOutcome;
  message: string;
}

export interface LeadListFilters {
  status?: LeadStatus;
  source?: LeadSource;
  vendor?: string;
  limit?: number;
  offset?: number;
}

/**
 * Leads pipeline service.
 *
 * Public endpoint `submitAccessRequest` is the form-primary inbound
 * acquisition channel (locked 2026-05-11). Admin endpoints are
 * manager-only and backed by the leads admin router.
 */
class LeadsService {
  private get api(): AxiosInstance {
    return authService.getApi();
  }

  /** Public submit. Always returns 201 + an outcome tag. The UI
   *  branches on outcome to show success vs. "already pending" vs.
   *  the internal-domain block page. NEVER throws on the existing-
   *  user / already-pending paths — those are NOT errors, they are
   *  expected outcomes (anti-enumeration). */
  async submitAccessRequest(body: AccessRequestPayload): Promise<AccessRequestResponse> {
    const res = await this.api.post<AccessRequestResponse>(
      '/api/v1/auth/access-requests',
      body,
    );
    return res.data;
  }

  /** Manager+: list leads with optional filters. */
  /**
   * Countries we cannot open an account for, for the sign-up path.
   *
   * Public by design: the point is that a prospect learns it BEFORE typing
   * anything, not from a refusal at the end.
   */
  async blockedCountries(): Promise<PublicBlockedCountry[]> {
    const res = await this.api.get<{ countries: PublicBlockedCountry[]; explanation: string }>(
      '/api/v1/reference/blocked-countries',
    );
    return res.data.countries;
  }

  async list(filters: LeadListFilters = {}): Promise<{ items: Lead[]; count: number }> {
    const params: Record<string, string | number> = {};
    if (filters.status) params.status_filter = filters.status;
    if (filters.source) params.source_filter = filters.source;
    if (filters.vendor) params.vendor_filter = filters.vendor;
    if (filters.limit) params.limit = filters.limit;
    if (filters.offset) params.offset = filters.offset;
    const res = await this.api.get<{ items: Lead[]; count: number }>(
      '/api/v1/admin/leads',
      { params },
    );
    return res.data;
  }

  /** Approve a pending lead → emits an invitation (+ enqueues email).
   *  Returns the invitation_id. Idempotent: re-calling on already-
   *  invited leads returns the existing invitation. */
  async approve(
    leadId: string,
    reviewNotes?: string,
  ): Promise<{ invitation_id: string; invitation_link: string | null }> {
    const res = await this.api.post<{ invitation_id: string; invitation_link: string | null }>(
      `/api/v1/admin/leads/${leadId}/approve`,
      { review_notes: reviewNotes ?? null },
    );
    return res.data;
  }

  /** PERMANENT rejection — the email is banned from re-submitting
   *  for life by a UNIQUE(lower(email)) constraint. The UI should
   *  surface a "this is permanent" confirmation step before calling
   *  this. `reviewNotes` is required (min 5 chars). */
  async reject(leadId: string, reviewNotes: string): Promise<void> {
    await this.api.post(
      `/api/v1/admin/leads/${leadId}/reject`,
      { review_notes: reviewNotes },
    );
  }
}

export default new LeadsService();
